import { Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { LucideChevronDown, LucideDownload, LucideX } from '@lucide/angular'
import { MatDialog } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { interval } from 'rxjs'

import { BigDownloadState, VideoDownloadService } from '../../services/video-download.service'
import { ViewportService } from '../../services/viewport.service'
import { BottomBarsService } from '../../directives/bottom-bar.directive'
import { OpenOverlaysService } from '../../services/open-overlays.service'

// The expiry countdown only has to be roughly right, and a ready row is the
// one place it is read — a minute is close enough to never lie by much.
const EXPIRY_TICK_MS = 60_000
// A toast slides in; its resting place is known once the entry animation has run.
const TOAST_SETTLE_MS = 400
// The gap kept between the top of the toast bar and the bottom of the stack.
const TOAST_GAP_PX = 8
// How far above the screen's foot a page bar may end and still count as "at the bottom": a fixed
// bar floats 12px plus the home-bar inset (~46px) up. A sticky bar scrolled away mid-page ends
// far higher, and is not in the stack's way (Release 7, 04/11/2026, PLAYER2-02).
const BAR_AT_FOOT_PX = 96

/**
 * The persistent corner stack of tracked downloads — one row each. Lives at
 * app root so it survives the player closing, and floats above the fullscreen
 * player too — minutes of preparation deserve visible progress everywhere.
 * While a dialog (the player) is open it lifts to the top corner so it never
 * covers the video controls or the trimmer's buttons.
 *
 * It collapses to a chip rather than closing: a reel is twenty-odd minutes of
 * server work, and the old X stopped the polling with it, leaving the job
 * running with no way back to it short of a reload. Rows now leave on their
 * own — a ready one when its link expires — and only a settled row can be
 * cleared by hand.
 */
@Component({
  selector: 'app-download-pill',
  standalone: true,
  imports: [LucideChevronDown, LucideDownload, LucideX, MatProgressSpinnerModule],
  templateUrl: './download-pill.component.html',
  styleUrl: './download-pill.component.scss',
})
export class DownloadPillComponent {
  private videoDownloadService = inject(VideoDownloadService)
  private dialog = inject(MatDialog)
  private viewport = inject(ViewportService)
  private bottomBars = inject(BottomBarsService)
  /**
   * A sheet, menu or panel is open (Release 7, 04/11/2026, SHELL2-01): the stack steps aside. It
   * sits above the navbar's stacking context, so it covered the Apps / Account sheets and the
   * bell's inbox. Nothing stops while it is hidden; it is back the moment the sheet closes.
   */
  readonly overlayOpen = inject(OpenOverlaysService).anyOpen

  entries = this.videoDownloadService.trackedDownloads
  // Several playlists ticked at once (29/10/2026): the ones not sent to the server yet.
  waiting = computed(() => this.videoDownloadService.waitingPlaylists().length)
  hasOpenDialog = signal(false)
  collapsed = signal(false)
  /**
   * On a phone a toast is a full-width bar in the very spot the stack rests, and it sits on top
   * (its overlay is z-index 9999), so "Watch the corner for progress" covered the corner. While a
   * toast shows, the stack rises above it by this many pixels (mobile audit SHELL-01 / VIDEO-03,
   * 30/10/2026). Measured, not guessed: a toast can wrap to two lines and phones show two at once.
   */
  toastLift = signal(0)
  private toastObserver?: MutationObserver
  private toastTimer?: ReturnType<typeof setTimeout>
  private now = signal(Date.now())
  // Which finished jobs have already pulled the stack open. A file must never
  // sit unnoticed behind a chip, but re-collapsing has to stay possible.
  private announced = new Set<string>()

  constructor () {
    // A tracked download from a previous visit greets its owner right here.
    void this.videoDownloadService.recoverTracked()
    this.dialog.afterOpened.pipe(takeUntilDestroyed()).subscribe(
      () => this.hasOpenDialog.set(true))
    this.dialog.afterAllClosed.pipe(takeUntilDestroyed()).subscribe(
      () => this.hasOpenDialog.set(false))
    interval(EXPIRY_TICK_MS).pipe(takeUntilDestroyed()).subscribe(
      () => this.now.set(Date.now()))
    this.watchToasts()
    // The stack can appear (or the phone rotate) while a toast is already up.
    effect(() => {
      void this.entries().length
      void this.waiting()
      void this.viewport.isPhone()
      void this.bottomBars.bars()
      untracked(() => this.measureToasts())
    })

    effect(() => {
      // A failure earns the same treatment as a file: a reel that gave up
      // twenty minutes ago raised one toast nobody was there for, and the
      // chip is otherwise the last place it would be mentioned.
      const settled = this.entries().filter(s => s.phase === 'ready' || s.phase === 'failed')
      if (settled.some(s => !this.announced.has(s.requestId))) this.collapsed.set(false)
      for (const s of settled) this.announced.add(s.requestId)
    })
  }

  /**
   * The toast library adds its container to <body> on the first toast and keeps it, so <body> is
   * watched only for that one child (not its whole subtree, which is the entire app); after that,
   * only the toast container itself.
   */
  private watchToasts (): void {
    if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return
    const onToasts = () => this.measureToasts()
    const attach = (): boolean => {
      const host = document.querySelector('hot-toast-container')
      if (!host) return false
      this.toastObserver?.disconnect()
      this.toastObserver = new MutationObserver(onToasts)
      this.toastObserver.observe(host, { childList: true, subtree: true })
      onToasts()
      return true
    }
    let bodyObserver: MutationObserver | undefined
    if (!attach()) {
      bodyObserver = new MutationObserver(() => { if (attach()) bodyObserver?.disconnect() })
      bodyObserver.observe(document.body, { childList: true })
    }
    // A sticky page bar (the player Videos "Play my reel" bar) reaches the foot of the screen, or
    // leaves it, as the page scrolls — so the lift is re-measured on scroll, once a frame at most,
    // and only while a page bar is registered and a download is showing (Release 7, 04/11/2026).
    let frame = 0
    const onScroll = () => {
      if (frame || !this.bottomBars.bars().length || (!this.entries().length && !this.waiting())) return
      frame = requestAnimationFrame(() => {
        frame = 0
        this.toastLift.set(this.currentToastLift())
      })
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    inject(DestroyRef).onDestroy(() => {
      bodyObserver?.disconnect()
      this.toastObserver?.disconnect()
      if (this.toastTimer) clearTimeout(this.toastTimer)
      document.removeEventListener('scroll', onScroll, { capture: true })
      if (frame) cancelAnimationFrame(frame)
    })
  }

  /** Now and again once the toast has slid into place. */
  private measureToasts (): void {
    this.toastLift.set(this.currentToastLift())
    if (this.toastTimer) clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => this.toastLift.set(this.currentToastLift()), TOAST_SETTLE_MS)
  }

  private currentToastLift (): number {
    // Desktop toasts sit in the corner beside a stack that is narrower than the screen; the
    // collision is a phone one, and nothing to lift for when no download is showing.
    if (!this.viewport.isPhone() || (!this.entries().length && !this.waiting())) return 0
    const height = window.innerHeight
    let top = height
    for (const bar of Array.from(document.querySelectorAll<HTMLElement>('hot-toast-container .hot-toast-bar-base'))) {
      const r = bar.getBoundingClientRect()
      // Bottom-half toasts only: one leaving upward or off screen is not in the way.
      if (r.height > 0 && r.bottom <= height + 1 && r.top > height / 2) top = Math.min(top, r.top)
    }
    // A page's own bar at the foot of the screen (Playlists' Select bar, the Videos reel bar): the
    // stack rises above it too — but only while it is on screen and at the foot. A sticky bar
    // scrolled up the page with its list is not in the way (Release 7, 04/11/2026, PLAYER2-02).
    for (const bar of this.bottomBars.bars()) {
      const r = bar.getBoundingClientRect()
      if (r.height > 0 && r.top > height / 2 && r.top < height && r.bottom >= height - BAR_AT_FOOT_PX) {
        top = Math.min(top, r.top)
      }
    }
    return top < height ? Math.round(height - top + TOAST_GAP_PX) : 0
  }

  setCollapsed (collapsed: boolean): void {
    this.collapsed.set(collapsed)
  }

  // The chip's own line: what is behind it, without opening it.
  chipTitle (): string {
    const working = this.entries().filter(
      s => s.phase === 'preparing' || s.phase === 'queued').length
    const ready = this.entries().filter(s => s.phase === 'ready').length
    const failed = this.entries().filter(s => s.phase === 'failed').length
    const parts: string[] = []
    if (working) parts.push(`${working} preparing`)
    if (ready) parts.push(`${ready} ready`)
    if (failed) parts.push(`${failed} failed`)
    return parts.length ? `Downloads — ${parts.join(', ')}` : 'Downloads'
  }

  statusLine (s: BigDownloadState): string {
    // A run of failed polls is not a failed job — the encode may well be
    // finishing — but this tab can no longer see it, and the row said nothing
    // about that for the rest of the horizon.
    if (s.lostContact && (s.phase === 'preparing' || s.phase === 'queued')) {
      return 'Lost contact with the server — it may still be preparing'
    }
    switch (s.phase) {
      case 'queued': return 'Queued — waiting for a slot'
      case 'preparing': {
        if (s.branding) {
          const eta = s.etaMinutes ? ` · about ${s.etaMinutes} min` : ''
          return `Adding the SCIOS scoreboard — ${s.pct}%${eta}`
        }
        if (s.kind !== 'playlist') return `Preparing ${s.kind === 'range' ? 'video' : 'clip'} — ${s.pct}%`
        // A clip counter is true at every moment; a shrinking time estimate is
        // a promise re-broken every poll.
        const clip = s.progressClip && s.totalClips
          ? `clip ${s.progressClip} of ${s.totalClips} · ` : ''
        return `Preparing reel — ${clip}${s.pct}%`
      }
      case 'ready': {
        if (s.kind !== 'playlist') return s.saved ? 'Saved — ready here too' : 'Ready — waiting for you'
        const census = this.censusParts(s)
        if (census.length) return `Ready · ${census.join(' · ')}`
        return `Ready${this.sizePart(s)}${this.expiryPart(s)}`
      }
      case 'failed': {
        if (s.kind === 'playlist') {
          return s.errorKind === 'video_unavailable'
            ? "None of these clips could be prepared — their recordings aren't available"
            : 'Preparing the reel failed — try again'
        }
        return s.errorKind !== 'video_unavailable'
          ? 'Preparation failed — try again'
          : s.kind === 'range'
            ? "Can't prepare a mid-match range here — Full match still works"
            : "Video isn't available for this clip"
      }
    }
  }

  // The line that answers "can I close this?" — honestly, per phase.
  subLine (s: BigDownloadState): string {
    switch (s.phase) {
      case 'queued':
      case 'preparing':
        return s.lostContact
          ? 'Nothing was cancelled — reopen this page later and it will be back here, finished or not.'
          : this.videoDownloadService.tapToSave
            ? 'Keep the app open — when it is ready, tap Save here. It waits here if you come back later.'
            : 'Keep the app open — it saves itself when ready, and waits here if you come back later.'
      case 'ready': {
        // A census takes the status line, so the file's own details move down
        // here rather than being dropped. What must not move with them is
        // whether the file has actually been saved: a recovered row and a tab
        // that lost the claim are both Ready and neither is saving.
        if (this.censusParts(s).length) {
          const details = `${this.sizePart(s)}${this.expiryPart(s)}`.replace(/^ · /, '')
          const lead = s.saved ? 'Already on your disk' : `${this.tapWord()} Save to collect it`
          return details ? `${lead} — ${details}.` : `${lead}.`
        }
        return s.saved
          ? 'Already on your disk — Save again fetches another copy while the link lasts.'
          : `${this.tapWord()} Save to collect it — the link lives about two hours after it was ready.`
      }
      case 'failed':
        return ''
    }
  }

  private tapWord (): string {
    return this.videoDownloadService.tapToSave ? 'Tap' : 'Click'
  }

  // Only a settled row can be cleared by hand: dismissing a working one used
  // to stop its polling and orphan the job. A row that has lost contact is
  // settled enough — its record survives, so the next visit resurfaces it.
  canDismiss (s: BigDownloadState): boolean {
    return s.phase === 'ready' || s.phase === 'failed' || s.lostContact === true
  }

  async onSave (s: BigDownloadState): Promise<void> {
    if (s.phase !== 'ready') return
    // Safari and iPhones save only inside the tap, so the known link is
    // followed before anything is awaited.
    const tapped = this.videoDownloadService.tapToSave
      ? this.videoDownloadService.saveFromTap(s.requestId)
      : 'unknown'
    const outcome = tapped !== 'unknown'
      ? tapped
      : await this.videoDownloadService.openVerified(s.requestId)
    if (outcome === 'gone') this.videoDownloadService.clearBigDownload(s.requestId)
    else this.videoDownloadService.markSaved(s.requestId)
  }

  onDismiss (s: BigDownloadState): void {
    if (!this.canDismiss(s)) return
    // A job that lost contact may still be encoding, and its row promises it
    // will be back next visit — so only a settled row is forgotten for good.
    if (s.phase === 'ready' || s.phase === 'failed') {
      this.videoDownloadService.clearBigDownload(s.requestId)
    } else {
      this.videoDownloadService.dismissBigDownload(s.requestId)
    }
  }

  /**
   * What the file does and does not contain, in the two counts the server
   * keeps apart. `totalClips` is already what the server set out to encode —
   * the clips dropped for access never entered it — so those are reported as
   * their own number rather than folded into the "of", which would understate
   * the loss on a long playlist.
   */
  private censusParts (s: BigDownloadState): string[] {
    if (s.kind !== 'playlist') return []
    const total = s.totalClips ?? 0
    const unencodable = s.skippedUnencodable ?? 0
    const noAccess = s.skippedNoAccess ?? 0
    const parts: string[] = []
    if (total > 0 && unencodable > 0) {
      parts.push(`${total - unencodable} of ${total} clips`, `${unencodable} couldn't be prepared`)
    } else if (total > 0 && noAccess > 0) {
      parts.push(`${total} ${total === 1 ? 'clip' : 'clips'}`)
    }
    if (noAccess > 0) parts.push(`${noAccess} not from your matches`)
    // D3: made without the video worker, so without the clips' text (CC, 13/10/2026).
    if (s.captionsLeftOut) parts.push('text left out this time — download again to get it')
    return parts
  }

  private sizePart (s: BigDownloadState): string {
    if (!s.fileSize) return ''
    const mb = s.fileSize / (1024 * 1024)
    return ` · ${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`
  }

  private expiryPart (s: BigDownloadState): string {
    if (!s.expiresAt) return ''
    const ms = new Date(s.expiresAt).getTime() - this.now()
    if (Number.isNaN(ms) || ms <= 0) return ''
    const minutes = Math.floor(ms / 60_000)
    if (minutes < 1) return ' · link expires in under a minute'
    const window = minutes < 60
      ? `${minutes}m`
      : `${Math.floor(minutes / 60)}h ${minutes % 60}m`
    return ` · link expires in ${window}`
  }
}
